import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { 
  Key, Plus, Trash2, Copy, Check, RefreshCw, Loader2, 
  Eye, EyeOff, Globe, ExternalLink, Code, ToggleLeft, ToggleRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CatalogApiTab = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyDesc, setNewKeyDesc] = useState("");
  const [showNewKey, setShowNewKey] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      const response = await fetch(`${API}/catalog-api/keys`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setKeys(data.keys || []);
      }
    } catch (error) {
      console.error("Error loading keys:", error);
    }
    setLoading(false);
  };

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Introduce un nombre para la API key");
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/catalog-api/keys/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: newKeyName.trim(),
          description: newKeyDesc.trim() || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowNewKey(data.api_key);
        setNewKeyName("");
        setNewKeyDesc("");
        loadKeys();
        toast.success("API key creada");
      } else {
        toast.error(data.detail || "Error al crear API key");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
    setCreating(false);
  };

  const deleteKey = async (keyId) => {
    if (!confirm("¿Eliminar esta API key? Los clientes que la usen perderán acceso.")) return;

    try {
      const response = await fetch(`${API}/catalog-api/keys/${keyId}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("API key eliminada");
        loadKeys();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const toggleKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/catalog-api/keys/${keyId}/toggle`, {
        method: "PUT",
        credentials: "include"
      });

      if (response.ok) {
        loadKeys();
        toast.success("Estado actualizado");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const getShareableLink = (apiKey) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/catalog/${apiKey}`;
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalog-api-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">API del Catálogo</h2>
          <p className="text-muted-foreground">Crea API keys para compartir tu catálogo con clientes</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadKeys}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* New Key Created Alert */}
      {showNewKey && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader>
            <CardTitle className="text-base text-green-600 dark:text-green-400 flex items-center gap-2">
              <Check className="w-5 h-5" />
              ¡API Key creada!
            </CardTitle>
            <CardDescription>
              Comparte este enlace con tu cliente. Podrá ver y descargar el catálogo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Enlace para compartir:</Label>
              <div className="flex gap-2">
                <Input 
                  value={getShareableLink(showNewKey)} 
                  readOnly 
                  className="font-mono text-sm"
                />
                <Button onClick={() => copyToClipboard(getShareableLink(showNewKey), "sharelink")}>
                  {copied === "sharelink" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">API Key (para uso técnico):</Label>
              <div className="flex gap-2">
                <Input 
                  value={showNewKey} 
                  readOnly 
                  className="font-mono text-sm text-muted-foreground"
                />
                <Button variant="outline" onClick={() => copyToClipboard(showNewKey, "newkey")}>
                  {copied === "newkey" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowNewKey(null)}>
              Cerrar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create New Key */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-500" />
            Crear Nueva API Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                placeholder="Ej: Cliente ABC"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Input
                placeholder="Ej: Acceso solo lectura para integración"
                value={newKeyDesc}
                onChange={(e) => setNewKeyDesc(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={createKey} disabled={creating || !newKeyName.trim()}>
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Key className="w-4 h-4 mr-2" />
            )}
            Crear API Key
          </Button>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-5 h-5 text-purple-500" />
            API Keys ({keys.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {keys.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No hay API keys creadas. Crea una para compartir tu catálogo.
            </p>
          ) : (
            <div className="space-y-3">
              {keys.map((key) => (
                <div 
                  key={key.key_id} 
                  className={`p-4 rounded-lg border ${key.is_active ? 'bg-muted/30' : 'bg-red-500/5 border-red-500/20'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{key.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          key.is_active 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {key.is_active ? 'Activa' : 'Desactivada'}
                        </span>
                      </div>
                      {key.description && (
                        <p className="text-sm text-muted-foreground mt-1">{key.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="font-mono">{key.api_key}</span>
                        <span>•</span>
                        <span>{key.requests_count || 0} requests</span>
                        {key.last_used && (
                          <>
                            <span>•</span>
                            <span>Último uso: {new Date(key.last_used).toLocaleDateString()}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleKey(key.key_id)}
                        title={key.is_active ? "Desactivar" : "Activar"}
                      >
                        {key.is_active ? (
                          <ToggleRight className="w-4 h-4 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-4 h-4 text-gray-400" />
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => deleteKey(key.key_id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* API Documentation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Code className="w-5 h-5 text-orange-500" />
            Documentación de la API
          </CardTitle>
          <CardDescription>
            Endpoints disponibles para acceder al catálogo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {/* Endpoint 1 */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold px-2 py-1 bg-green-500 text-white rounded">GET</span>
                <code className="text-sm">/api/catalog-api/v1/catalog</code>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Obtener productos del catálogo con filtros opcionales.</p>
              <div className="text-xs space-y-1">
                <p><strong>Parámetros:</strong></p>
                <ul className="list-disc list-inside text-muted-foreground ml-2">
                  <li><code>api_key</code> (requerido) - Tu API key</li>
                  <li><code>country</code> - Filtrar por país</li>
                  <li><code>category</code> - Filtrar por categoría</li>
                  <li><code>min_price</code> / <code>max_price</code> - Rango de precios</li>
                  <li><code>casino</code>, <code>cbd</code>, <code>gambling</code> - Filtros booleanos</li>
                  <li><code>limit</code> (1-1000) - Límite de resultados</li>
                  <li><code>offset</code> - Para paginación</li>
                </ul>
              </div>
              <div className="mt-3">
                <p className="text-xs font-medium mb-1">Ejemplo:</p>
                <code className="text-xs bg-background p-2 rounded block overflow-x-auto">
                  {baseUrl}/api/catalog-api/v1/catalog?api_key=TU_API_KEY&limit=100
                </code>
              </div>
            </div>

            {/* Endpoint 2 */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold px-2 py-1 bg-green-500 text-white rounded">GET</span>
                <code className="text-sm">/api/catalog-api/v1/catalog/stats</code>
              </div>
              <p className="text-sm text-muted-foreground">Estadísticas del catálogo (total, por país, precios).</p>
            </div>

            {/* Endpoint 3 */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold px-2 py-1 bg-green-500 text-white rounded">GET</span>
                <code className="text-sm">/api/catalog-api/v1/catalog/countries</code>
              </div>
              <p className="text-sm text-muted-foreground">Lista de países disponibles en el catálogo.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CatalogApiTab;
